"use strict";
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.VersionResolver = void 0;
const semver = require("semver");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const InstallIdUtilities_1 = require("../Utils/InstallIdUtilities");
const WebRequestWorkerSingleton_1 = require("../Utils/WebRequestWorkerSingleton");
class VersionResolver {
    constructor(context, webWorker) {
        this.context = context;
        this.releasesUrl = 'https://builds.dotnet.microsoft.com/dotnet/release-metadata/releases-index.json';
        this.webWorker = webWorker !== null && webWorker !== void 0 ? webWorker : WebRequestWorkerSingleton_1.WebRequestWorkerSingleton.getInstance();
    }
    /**
     * @remarks
     * Use the release.json manifest that contains the newest version of the SDK and Runtime for each major.minor of .NET to get the available versions.
     * Relies on the context listRuntimes to tell if it should get runtime or sdk versions.
     *
     * @params
     * webWorker - This class can use its own web-worker or a custom one for testing purposes.
     *
     * @returns
     * IDotnetListVersionsResult of versions available.
     *
     * @throws
     * Exception if the API service for releases-index.json is unavailable.
     */
    GetAvailableDotnetVersions(commandContext) {
        return __awaiter(this, void 0, void 0, function* () {
            const getSdkVersions = !(commandContext === null || commandContext === void 0 ? void 0 : commandContext.listRuntimes);
            const availableVersions = [];
            const response = yield this.webWorker.getCachedData(this.releasesUrl, this.context);
            return new Promise((resolve, reject) => {
                var _a, _b, _c, _d;
                if (!response) {
                    const offlineError = new Error('Unable to connect to the index server: Cannot find .NET versions.');
                    this.context.eventStream.post(new EventStreamEvents_1.DotnetOfflineFailure(offlineError, (0, InstallIdUtilities_1.getInstallFromContext)(this.context)));
                    reject(offlineError);
                }
                else {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const releases = response === null || response === void 0 ? void 0 : response['releases-index'];
                    for (const release of releases) {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        if ((release === null || release === void 0 ? void 0 : release['release-type']) === 'lts' || (release === null || release === void 0 ? void 0 : release['release-type']) === 'sts') {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                            availableVersions === null || availableVersions === void 0 ? void 0 : availableVersions.push({
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                supportStatus: (_a = release === null || release === void 0 ? void 0 : release['release-type']) !== null && _a !== void 0 ? _a : 'sts',
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                supportPhase: (_b = release === null || release === void 0 ? void 0 : release['support-phase']) !== null && _b !== void 0 ? _b : 'eol',
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                version: (_c = release === null || release === void 0 ? void 0 : release[getSdkVersions ? 'latest-sdk' : 'latest-runtime']) !== null && _c !== void 0 ? _c : '0.0',
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                                channelVersion: (_d = release === null || release === void 0 ? void 0 : release['channel-version']) !== null && _d !== void 0 ? _d : '0.0'
                            });
                        }
                    }
                }
                resolve(availableVersions);
            });
        });
    }
    getFullVersion(version, mode) {
        return __awaiter(this, void 0, void 0, function* () {
            let releasesVersions;
            try {
                releasesVersions = yield this.getReleasesInfo(mode);
            }
            catch (error) {
                // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                throw new EventStreamEvents_1.EventBasedError(error, error === null || error === void 0 ? void 0 : error.message, error === null || error === void 0 ? void 0 : error.stack);
            }
            return new Promise((resolve, reject) => {
                var _a;
                try {
                    const versionResult = this.resolveVersion(version, releasesVersions);
                    this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionResolutionCompleted(version, versionResult));
                    resolve(versionResult);
                }
                catch (error) {
                    // Remove this when https://github.com/typescript-eslint/typescript-eslint/issues/2728 is done
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    this.context.eventStream.post(new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', (_a = error === null || error === void 0 ? void 0 : error.message) !== null && _a !== void 0 ? _a : ''), (0, InstallIdUtilities_1.getAssumedInstallInfo)(version, mode)));
                    reject(error);
                }
            });
        });
    }
    resolveVersion(version, releases) {
        var _a, _b;
        this.validateVersionInput(version);
        // Search for the specific version
        let matchingVersion = releases.filter((availableVersions) => availableVersions.version === version);
        // If a x.y version is given, just find that instead (which is how almost all requests are given atm)
        if (!matchingVersion || ((_a = matchingVersion === null || matchingVersion === void 0 ? void 0 : matchingVersion.length) !== null && _a !== void 0 ? _a : 0) < 1) {
            matchingVersion = releases.filter((availableVersions) => availableVersions.channelVersion === version);
        }
        if (!matchingVersion || ((_b = matchingVersion === null || matchingVersion === void 0 ? void 0 : matchingVersion.length) !== null && _b !== void 0 ? _b : 0) < 1) {
            const err = new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', `The requested and or resolved version is invalid.`), (0, InstallIdUtilities_1.getAssumedInstallInfo)(version, this.context.acquisitionContext.mode));
            this.context.eventStream.post(err);
            throw err.error;
        }
        return matchingVersion[0].version;
    }
    validateVersionInput(version) {
        let parsedVer;
        try {
            parsedVer = semver.coerce(version);
        }
        catch (err) {
            parsedVer = null;
        }
        if (!parsedVer || (version.split('.').length !== 2 && version.split('.').length !== 3)) {
            const err = new EventStreamEvents_1.DotnetVersionResolutionError(new EventStreamEvents_1.EventCancellationError('DotnetVersionResolutionError', `An invalid version was requested. Version: ${version}`), (0, InstallIdUtilities_1.getAssumedInstallInfo)(version, this.context.acquisitionContext.mode));
            this.context.eventStream.post(err);
            throw err.error;
        }
    }
    getReleasesInfo(mode) {
        return __awaiter(this, void 0, void 0, function* () {
            const apiContext = { listRuntimes: mode === 'runtime' || mode === 'aspnetcore' };
            const response = yield this.GetAvailableDotnetVersions(apiContext);
            if (!response) {
                const err = new EventStreamEvents_1.DotnetInvalidReleasesJSONError(new EventStreamEvents_1.EventBasedError('DotnetInvalidReleasesJSONError', `We could not reach the releases API ${this.releasesUrl} to download dotnet, is your machine offline or is this website down?`), (0, InstallIdUtilities_1.getInstallFromContext)(this.context));
                this.context.eventStream.post(err);
                throw err.error;
            }
            return response;
        });
    }
}
exports.VersionResolver = VersionResolver;
//# sourceMappingURL=VersionResolver.js.map